import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { logger } from '../utils/logger';

export interface SaveStatusState {
  isSaving: boolean;
  lastSavedAt: number | null; // epoch ms
  notifySaveStart: () => void;
  notifySaveEnd: () => void;
}

const SaveStatusContext = createContext<SaveStatusState | undefined>(undefined);

export const SaveStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const savingCountRef = useRef(0);

  const notifySaveStart = useCallback(() => {
    savingCountRef.current += 1;
    setIsSaving(true);
  }, []);

  const notifySaveEnd = useCallback(() => {
    const timestamp = Date.now();
    // DEBUG: warn so trace appears in production (logger.debug is no-op in PROD)
    logger.warn('[SAVE_TRACE] notifySaveEnd called', { timestamp }, 'SaveStatusContext');
    savingCountRef.current = Math.max(0, savingCountRef.current - 1);
    if (savingCountRef.current === 0) {
      setIsSaving(false);
      setLastSavedAt(timestamp);
      logger.warn('[SAVE_TRACE] SaveStatusContext state update', { lastSavedAt: timestamp }, 'SaveStatusContext');
    } else {
      logger.warn('[SAVE_TRACE] SaveStatusContext state NOT updated (savingCount !== 0)', { savingCount: savingCountRef.current }, 'SaveStatusContext');
    }
  }, []);

  const value = useMemo<SaveStatusState>(() => ({ isSaving, lastSavedAt, notifySaveStart, notifySaveEnd }), [isSaving, lastSavedAt, notifySaveStart, notifySaveEnd]);

  return (
    <SaveStatusContext.Provider value={value}>
      {children}
    </SaveStatusContext.Provider>
  );
};

export function useSaveStatus(): SaveStatusState {
  const ctx = useContext(SaveStatusContext);
  if (!ctx) throw new Error('useSaveStatus must be used within SaveStatusProvider');
  return ctx;
}

